import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Mail, Phone, type LucideIcon } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@shared-kernel/primary/shared/shadcn/ui/avatar";
import { Card } from "@shared-kernel/primary/shared/shadcn/ui/card";
import { Button } from "@shared-kernel/primary/shared/shadcn/ui/button";
import { cn } from "@components/utils/utils";
import { formatPhoneNumber } from "@utils/formatting";

export interface UserSummaryStat {
  icon: LucideIcon;
  label: string;
  primary: ReactNode;
  secondary?: ReactNode;
}

interface AvatarProps {
  src?: string;
  fallback: string;
}

interface Props {
  fullName: string;
  email: string | null;
  mobile: string | null;
  userDetailUrl: string;
  avatar?: AvatarProps;
  stats?: UserSummaryStat[];
}

const DASH = "—";
const linkClass = "text-foreground hover:underline";

const GRID_COLS_BY_LENGTH: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
};

export const UserSummaryCard = ({ fullName, email, mobile, userDetailUrl, avatar, stats = [] }: Props) => {
  const gridCols = GRID_COLS_BY_LENGTH[stats.length] ?? "grid-cols-3";

  return (
    <Card className="mb-6 overflow-hidden">
      <div className="flex items-center gap-4 px-5 py-4">
        {avatar && (
          <Avatar className="size-12 shrink-0">
            {avatar.src && <AvatarImage src={avatar.src} alt={fullName} />}
            <AvatarFallback className="bg-muted text-muted-foreground">{avatar.fallback}</AvatarFallback>
          </Avatar>
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h2 className="text-base font-semibold leading-snug">{fullName}</h2>
          <div className="flex min-w-0 items-center gap-1.5 text-sm text-muted-foreground">
            <Mail className="size-3.5 shrink-0" />
            {email ? (
              <a href={`mailto:${email}`} className={cn("truncate", linkClass)}>
                {email}
              </a>
            ) : (
              <span>{DASH}</span>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Phone className="size-3.5 shrink-0" />
            {mobile ? (
              <a href={`tel:${mobile}`} className={linkClass}>
                {formatPhoneNumber(mobile)}
              </a>
            ) : (
              <span>{DASH}</span>
            )}
          </div>
        </div>
        <Button asChild variant="outline" size="sm" className="shrink-0">
          <Link to={userDetailUrl}>
            <ExternalLink className="mr-2 size-4" />
            Voir l&apos;utilisateur
          </Link>
        </Button>
      </div>
      {stats.length > 0 && (
        <div className={cn("grid border-t bg-muted/30", gridCols)}>
          {stats.map(({ icon: Icon, label, primary, secondary }, idx) => (
            <div key={label} className={cn("space-y-1.5 px-5 py-3.5", idx < stats.length - 1 && "border-r")}>
              <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                <Icon className="mr-1 inline size-3 align-[-2px]" />
                {label}
              </p>
              <div className="text-sm font-medium">{primary}</div>
              {secondary !== undefined && <div className="text-xs text-muted-foreground">{secondary}</div>}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
